import React, { useState, useEffect } from 'react';
import { propositionsService } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Calendar, Mail, Send, Users, CheckCircle, Clock, AlertCircle, Eye } from 'lucide-react';
import PropositionsModal from './PropositionsModal';

const PropositionsManager = () => {
  const [activeSession, setActiveSession] = useState(null);
  const [propositions, setPropositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newSessionDate, setNewSessionDate] = useState('');
  const [showPropositionsModal, setShowPropositionsModal] = useState(false);
  const { toast } = useToast();

  // Charger la session active au démarrage
  useEffect(() => {
    loadActiveSession();
  }, []);

  const loadActiveSession = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await propositionsService.getActiveSession();
      
      if (response.active_session) {
        setActiveSession(response.active_session);
        // Charger les propositions si session existe
        loadPropositions(response.active_session.session_token);
      } else {
        setActiveSession(null);
      }
    } catch (error) {
      console.error('Error loading active session:', error);
      
      // Gestion gracieuse des erreurs - ne pas bloquer l'interface
      if (error.response?.status === 404) {
        setError("Fonctionnalité en cours de déploiement");
      } else if (error.response?.status >= 500) {
        setError("Service temporairement indisponible");
      } else {
        setError("Fonctionnalité des propositions non disponible");
      }
      
      setActiveSession(null);
    } finally {
      setLoading(false);
    }
  };

  const loadPropositions = async (sessionToken) => {
    try {
      const response = await propositionsService.listPropositions(sessionToken);
      setPropositions(response.propositions || []);
    } catch (error) {
      console.error('Error loading propositions:', error);
    }
  };

  const handleCreateSession = async () => {
    if (!newSessionDate) {
      toast({
        title: "Date requise",
        description: "Veuillez sélectionner une date de comité",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);
      const session = await propositionsService.createSession(newSessionDate);
      
      toast({
        title: "Session créée",
        description: `Session de collecte créée pour le ${new Date(newSessionDate).toLocaleDateString('fr-FR')}`,
        variant: "default"
      });
      
      setActiveSession({
        ...session,
        propositions_count: 0
      });
      setPropositions([]);
      setShowCreateForm(false);
      setNewSessionDate('');
      
    } catch (error) {
      console.error('Error creating session:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer la session",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSession = async () => {
    if (!activeSession) return;
    
    try {
      setLoading(true);
      // Appeler l'API pour fermer la session
      await propositionsService.closeSession(activeSession.session_token);
      
      toast({
        title: "Collecte clôturée",
        description: "La collecte de propositions a été fermée avec succès",
        variant: "default"
      });
      
      // Recharger pour voir l'état après fermeture
      setActiveSession(null);
      setPropositions([]);
      
    } catch (error) {
      console.error('Error closing session:', error);
      toast({
        title: "Erreur",
        description: "Impossible de clôturer la collecte",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async () => {
    try {
      setLoading(true);
      const response = await propositionsService.sendReminder();
      
      toast({
        title: "Email envoyé",
        description: `Rappel envoyé à ${response.recipients_count} membre(s)`,
        variant: "default"
      });
      
      // Marquer que l'email a été envoyé
      setActiveSession(prev => ({
        ...prev,
        email_envoye: true
      }));
      
    } catch (error) {
      console.error('Error sending reminder:', error);
      
      let errorMessage = "Impossible d'envoyer l'email de rappel";
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      toast({
        title: "Erreur d'envoi",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getSessionStatus = () => {
    if (!activeSession) return null;
    
    if (activeSession.email_envoye) {
      return {
        icon: <CheckCircle className="w-4 h-4 text-green-600" />,
        text: "Email envoyé",
        color: "text-green-600"
      };
    } else {
      return {
        icon: <Clock className="w-4 h-4 text-orange-600" />,
        text: "En attente d'envoi",
        color: "text-orange-600"
      };
    }
  };

  if (loading && !activeSession) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
            Propositions d'ordre du jour
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Chargement...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state - show minimal error without blocking
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
            Propositions d'ordre du jour
          </CardTitle>
          <CardDescription className="text-sm">
            Collectez les propositions des membres pour le prochain comité
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <AlertCircle className="w-8 h-8 text-orange-500 mx-auto mb-3" />
            <p className="text-sm text-gray-600 mb-3">{error}</p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={loadActiveSession}
              disabled={loading}
            >
              Réessayer
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
          Propositions d'ordre du jour
        </CardTitle>
        <CardDescription className="text-sm">
          Collectez les propositions des membres pour le prochain comité
        </CardDescription>
        
        {/* Guide workflow - Mobile Optimized */}
        <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200 mt-4">
          <h4 className="text-sm font-medium text-blue-900 mb-3">💡 Comment ça marche :</h4>
          <div className="space-y-3">
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">1</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-blue-900">Créez une collecte pour le prochain comité</p>
                <p className="text-xs text-blue-700 mt-1">Définissez la date du comité cible. Une session unique est créée avec un lien de collecte.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">2</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-blue-900">Envoyez le rappel aux membres</p>
                <p className="text-xs text-blue-700 mt-1">Un email automatique avec le lien de soumission est envoyé à tous les membres actifs.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">3</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-blue-900">Les membres soumettent leurs propositions</p>
                <p className="text-xs text-blue-700 mt-1">Formulaire public avec titre et détails optionnels. Confirmation par email.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">4</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-green-900">Intégration automatique dans les PV</p>
                <div className="mt-1 space-y-2">
                  <div className="bg-green-100 p-2 rounded text-xs">
                    <p className="font-medium text-green-800">🆕 Nouveau PV :</p>
                    <p className="text-green-700">Toutes les propositions s'intègrent automatiquement à l'ouverture</p>
                  </div>
                  <div className="bg-blue-100 p-2 rounded text-xs">
                    <p className="font-medium text-blue-800">📝 PV en cours d'édition :</p>
                    <p className="text-blue-700">Notification discrète des nouvelles propositions avec aperçu et boutons d'action</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">5</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-blue-900">Clôturez et préparez la suite</p>
                <p className="text-xs text-blue-700 mt-1">Fermez la collecte après le comité et créez une nouvelle session pour le prochain.</p>
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-2 sm:p-3 bg-blue-100 rounded-lg">
            <p className="text-xs text-blue-800">
              <span className="font-medium">🔔 Fonctionnalité temps réel :</span> Le système vérifie automatiquement les nouvelles propositions toutes les minutes lors de l'édition d'un PV existant.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!activeSession ? (
          // Aucune session active - Formulaire de création
          <div className="space-y-4">
            {!showCreateForm ? (
              <div className="text-center py-6">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                  Aucune collecte en cours
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Créez une session pour collecter les propositions des membres
                </p>
                <Button 
                  onClick={() => setShowCreateForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Créer une collecte
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="text-base sm:text-lg font-medium">Nouvelle collecte de propositions</h3>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Date du prochain comité
                  </label>
                  <input
                    type="date"
                    value={newSessionDate}
                    onChange={(e) => setNewSessionDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button 
                    onClick={handleCreateSession}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Créer la session
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewSessionDate('');
                    }}
                    className="w-full sm:w-auto"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Session active - Gestion
          <div className="space-y-4">
            {/* Informations de la session - Mobile Optimized */}
            <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200">
              <div className="flex flex-col gap-3">
                <div>
                  <h3 className="font-medium text-blue-900 text-sm sm:text-base">
                    Collecte pour le comité du {formatDate(activeSession.date_comite_cible)}
                  </h3>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2 text-sm">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4 text-blue-600" />
                      {activeSession.propositions_count} proposition(s) reçue(s)
                    </span>
                    {getSessionStatus() && (
                      <span className="flex items-center gap-1">
                        {getSessionStatus().icon}
                        <span className={getSessionStatus().color}>
                          {getSessionStatus().text}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    {!activeSession.email_envoye && (
                      <Button 
                        onClick={handleSendReminder}
                        disabled={loading}
                        className="bg-blue-600 hover:bg-blue-700 text-sm w-full sm:w-auto"
                      >
                        {loading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Envoyer rappel
                      </Button>
                    )}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => setShowPropositionsModal(true)}
                      disabled={loading}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50 text-sm w-full sm:w-auto"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Voir les propositions ({activeSession.propositions_count})
                    </Button>
                    
                    <Button 
                      variant="outline"
                      onClick={async () => {
                        await loadPropositions(activeSession.session_token);
                        await loadActiveSession(); // Recharge aussi les données de session
                      }}
                      disabled={loading}
                      className="text-sm w-full sm:w-auto"
                    >
                      Actualiser
                    </Button>
                    
                    {/* Boutons de gestion du cycle de vie */}
                    <Button 
                      variant="outline"
                      onClick={handleCloseSession}
                      disabled={loading}
                      className="text-red-600 border-red-200 hover:bg-red-50 text-sm w-full sm:w-auto"
                    >
                      Clôturer cette collecte
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Lien public - Mobile Optimized */}
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border">
              <h4 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">Lien de collecte :</h4>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <input
                  type="text"
                  value={`https://pvmanager-test.preview.emergentagent.com/propositions/${activeSession.session_token}`}
                  readOnly
                  className="flex-1 px-3 py-2 text-xs sm:text-sm bg-white border border-gray-300 rounded-md break-all"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(`https://pvmanager-test.preview.emergentagent.com/propositions/${activeSession.session_token}`);
                    toast({
                      title: "Copié",
                      description: "Lien copié dans le presse-papiers",
                      variant: "default"
                    });
                  }}
                  className="w-full sm:w-auto text-sm"
                >
                  Copier
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Les membres peuvent utiliser ce lien pour soumettre leurs propositions
              </p>
            </div>
          </div>
        )}

        {/* Modal pour afficher les propositions */}
        <PropositionsModal 
          isOpen={showPropositionsModal}
          onClose={() => setShowPropositionsModal(false)}
          sessionToken={activeSession?.session_token}
          sessionInfo={activeSession}
        />
      </CardContent>
    </Card>
  );
};

export default PropositionsManager;